import { useDocumentsByPersonne, DocumentCard } from "@features/rh/documents";
import { DocumentCardSkeleton } from "@features/rh/documents/components/DocumentCardSkeleton";
import { useCurrentUser } from "@lib/hooks/useCurrentUser";
import { createFileRoute } from "@tanstack/react-router";
import { FileText } from "lucide-react";
import type { Document } from "@features/rh/documents/components/DocumentCard";

// Type guard to check if data is an array
function isDocumentArray(data: unknown): data is Document[] {
  return Array.isArray(data);
}

export const Route = createFileRoute("/_app/(dashboard)/documents/me/")({
  staticData: {
    title: "Mes Documents",
    section: "Général",
    order: 70,
    showInTab: true,
    showInBreadcrumb: true,
  },
  component: DocumentsMeRoute,
});

function DocumentsMeRoute() {
  const { user, isUserLoading } = useCurrentUser();
  let personneId = null;
  if (user?.personne) {
    if (typeof user.personne === "object" && user.personne.id) {
      personneId = user.personne.id;
    } else if (typeof user.personne === "string") {
      // IRI format: "/api/personnes/16" -> extract ID
      const match = user.personne.match(/\/(\d+)$/);
      personneId = match ? parseInt(match[1], 10) : null;
    }
  } else if (user?.personneId) {
    personneId = user.personneId;
  }

  const {
    data: rawDocuments,
    isLoading,
    error,
  } = useDocumentsByPersonne(personneId);

  // Normalize documents data (handles both array and collection response)
  const documents = isDocumentArray(rawDocuments)
    ? rawDocuments
    : isDocumentArray((rawDocuments as unknown as { member?: Document[] })?.member)
      ? (rawDocuments as unknown as { member: Document[] }).member
      : [];

  // Loading state
  if (isUserLoading) {
    return (
      <div className="p-6 animate-fade-in">
        {/* Enhanced header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Mes Documents</h1>
            <p className="text-sm text-base-content/50">Chargement...</p>
          </div>
        </div>

        {/* Document skeleton grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <DocumentCardSkeleton
              key={i}
              style={{ animationDelay: `${(i - 1) * 50}ms` }}
            />
          ))}
        </div>
      </div>
    );
  }

  // No profile associated
  if (!personneId) {
    return (
      <div className="p-6 animate-fade-in">
        {/* Enhanced header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Mes Documents</h1>
            <p className="text-sm text-base-content/50">
              Aucun profil associé
            </p>
          </div>
        </div>

        {/* Empty state with hover shadow */}
        <div className="text-center py-12 transition-all duration-300 hover:shadow-lg rounded-xl bg-base-100 border border-base-200">
          <FileText className="w-12 h-12 text-base-content/30 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Aucun profil associé</h2>
          <p className="text-base-content/60">
            Vous n&apos;avez pas de profil employé associé à votre compte.
          </p>
        </div>
      </div>
    );
  }

  // Document count for header subtitle
  const documentCount = documents?.length ?? 0;
  const countLabel =
    documentCount === 0
      ? "Aucun document"
      : documentCount === 1
        ? "1 document"
        : `${documentCount} documents`;

  return (
    <div className="p-6 animate-fade-in">
      {/* Enhanced header with icon container */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <FileText className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-semibold">Mes Documents</h1>
          <p className="text-sm text-base-content/50">{countLabel}</p>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="alert alert-error mb-4">
          <span>Erreur lors du chargement des documents.</span>
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <DocumentCardSkeleton
              key={i}
              style={{ animationDelay: `${(i - 1) * 50}ms` }}
            />
          ))}
        </div>
      ) : documents && documents.length > 0 ? (
        /* Document grid with staggered animations */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc, index) => (
            <DocumentCard
              key={doc.id}
              document={doc}
              className="animate-slide-in-left"
              style={{
                animationDelay: `${index * 50}ms`,
              }}
            />
          ))}
        </div>
      ) : (
        /* Empty state with hover shadow */
        <div className="text-center py-12 transition-all duration-300 hover:shadow-lg rounded-xl bg-base-100 border border-base-200">
          <FileText className="w-12 h-12 text-base-content/30 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Aucun document</h2>
          <p className="text-base-content/60">
            Vous n&apos;avez pas encore de documents téléchargés.
          </p>
        </div>
      )}
    </div>
  );
}
